import React from "react";
import { useRenewal } from "../RenewalContext";
import { NOTES_PROMPT } from "../renewalCopy";
import { Card, CountedTextarea, StepHeading, StepNav, YesNo } from "../renewalUi";

interface Props {
  onBack: () => void;
  onNext: () => void;
}

const NOTES_MAX = 200;

/**
 * "Anything else to share?" — shared by the Renew and Not Sure flows.
 *
 * The wireframe showed multi-select chips; per the updates doc this is a
 * Yes/No question that reveals a 200-character textarea when answered Yes.
 */
const NotesStep: React.FC<Props> = ({ onBack, onNext }) => {
  const { draft, update } = useRenewal();
  const { notes } = draft;

  const setNotes = (patch: Partial<typeof notes>) =>
    update({ notes: { ...notes, ...patch } });

  // Answering "No" is a complete answer; "Yes" needs some text.
  const canContinue =
    notes.hasNotes === false ||
    (notes.hasNotes === true && notes.text.trim().length > 0);

  return (
    <>
      <StepHeading title={NOTES_PROMPT.title} subtitle={NOTES_PROMPT.subtitle} />

      <Card>
        <YesNo
          value={notes.hasNotes}
          onChange={(v) => setNotes({ hasNotes: v, text: v ? notes.text : "" })}
        />

        {notes.hasNotes === true && (
          <div className="mt-4">
            <label className="mb-1 block text-sm text-gray-600">
              What would you like us to pass along?
            </label>
            <CountedTextarea
              value={notes.text}
              onChange={(text) => setNotes({ text })}
              maxLength={NOTES_MAX}
              placeholder="Tell us more..."
            />
          </div>
        )}
      </Card>

      <StepNav onBack={onBack} onNext={onNext} nextDisabled={!canContinue} />
    </>
  );
};

export default NotesStep;
